import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress
} from '@mui/material';
import Input from '../../../components/Input';
import Select from '../../../components/Select';

const AddVerifyModeModal = ({ open, onClose, onConfirm, verifyModeData, editMode = false }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 1
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editMode && verifyModeData) {
      setFormData({
        id: verifyModeData.id,
        name: verifyModeData.name || '',
        description: verifyModeData.description || '',
        status: verifyModeData.status !== undefined ? verifyModeData.status : 1
      });
    } else if (!editMode) {
      setFormData({
        name: '',
        description: '',
        status: 1
      });
    }
    setErrors({});
  }, [editMode, verifyModeData, open]);

  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.name.trim() !== '';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('verifyModes.validation.nameRequired');
    } else if (formData.name.length > 200) {
      newErrors.name = t('verifyModes.validation.nameMaxLength');
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = t('verifyModes.validation.descriptionMaxLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // Validate field in real-time
    const newErrors = { ...errors };

    if (name === 'name') {
      if (!value.trim()) {
        newErrors.name = t('verifyModes.validation.nameRequired');
      } else if (value.length > 200) {
        newErrors.name = t('verifyModes.validation.nameMaxLength');
      } else {
        delete newErrors.name;
      }
    } else if (name === 'description') {
      if (value && value.length > 500) {
        newErrors.description = t('verifyModes.validation.descriptionMaxLength');
      } else {
        delete newErrors.description;
      }
    }

    setErrors(newErrors);
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    onConfirm(formData);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      id="verify-mode-dialog"
      data-testid="verify-mode-dialog"
    >
      <DialogTitle>
        {editMode ? t('verifyModes.editTitle') : t('verifyModes.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Input
            id="name"
            name="name"
            data-testid="verifyMode-modal-input-name"
            label={t('verifyModes.name')}
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            required
            disabled={loading}
          />
          <Input
            id="description"
            name="description"
            data-testid="verifyMode-modal-input-description"
            label={t('verifyModes.description')}
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
            error={!!errors.description}
            helperText={errors.description}
            disabled={loading}
          />
          <Select
            id="status"
            name="status"
            data-testid="verifyMode-modal-select-status"
            label={t('verifyModes.status.label')}
            value={formData.status}
            onChange={(e) => {
              const value = e.target ? e.target.value : e;
              setFormData((prev) => ({
                ...prev,
                status: value
              }));
            }}
            options={[
              { value: 0, label: t('verifyModes.status.inactive') },
              { value: 1, label: t('verifyModes.status.active') }
            ]}
            required
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="verifyMode-modal-button-cancel"
        >
          {t('verifyModes.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid() || Object.values(errors).some(error => error !== '') || loading}
          id="submit"
          name="submit"
          data-testid="verifyMode-modal-button-submit"
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : editMode ? (
            t('verifyModes.save')
          ) : (
            t('verifyModes.add')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddVerifyModeModal;
